import { LoaderContext } from 'hls.js';

/**
 * Creates a fetchSetup function for HLS.js with authentication support.
 *
 * ⚠️ The Hls instance you pass this to MUST also set `progressive: true`.
 * `fetchSetup` is honoured only by hls.js's fetch loader, and `progressive` is
 * what selects it — without it hls.js quietly uses XHR instead, this function is
 * never called, no Authorization header is attached, and every playlist and
 * segment request 401s. Nothing in the name hints at that, and the failure looks
 * like an auth or CORS problem rather than a config one. Every call site
 * (`VideoPreview`, `@lib/media/hlsFrameCapture`, the merge player's
 * `VideoComponent`) sets it for this reason.
 *
 * Supports dynamic token rotation by accepting either:
 *   - Static token (string): Token is captured at HLS creation time
 *   - Dynamic getter (function): Called on each HTTP request, enables token rotation
 *
 * Example with dynamic token (for token expiration handling):
 *   const tokenGetter = () => libraryPreview.getCachedPreviewToken(libraryId);
 *   fetchSetup: hlsFetchSetup(tokenGetter)
 *
 *   // When token is refreshed externally, HLS automatically uses the new token
 *
 * @param tokenGetter - Token string or getter function
 * @param useHeader - Use Authorization header (true) or query parameter (false)
 */
export const hlsFetchSetup = (
  tokenGetter: string | null | (() => string | null),
  useHeader = true,
) => {
  return (context: LoaderContext, initParams: RequestInit) => {
    let url = context.url;
    let headers: Record<string, string> = { ...(initParams.headers as Record<string, string>) };

    // Get token - call function if provided, otherwise use static value
    const accessToken = typeof tokenGetter === 'function' ? tokenGetter() : tokenGetter;

    if (accessToken) {
      if (useHeader) {
        // Use Authorization header (more secure, default behavior)
        headers['Authorization'] = `Bearer ${accessToken}`;
      } else {
        // Use query parameter (legacy fallback, not recommended)
        if (!url.includes('access_token=')) {
          const separator = url.includes('?') ? '&' : '?';
          url = `${url}${separator}access_token=${accessToken}`;
        }
      }
    }

    return new Request(url, {
      ...initParams,
      headers: {
        'Cache-Control': 'private, max-age=600',
        ...headers,
      },
      cache: 'default',
    });
  };
};
